/* ob-gallery-slide.js - direction-aware PUSH for every PDP main image gallery.
   Approved by Archie, motion signed off by Jarek in the prototype (24 Sep 2026).

   ONE engine, four galleries:
     - the tournament arm gallery (ob-pdp-test1..4-body, driven by ob-pdp-test1.js)
     - the fallback product gallery in sections/product.liquid (native scroll-snap track)
     - Impulse's Flickity product slider (main-product: brand-story, gift-card,
       preorder, product-landing)
     - the <media-gallery> element (main-product-high-variant)
   Every change - arrow, thumbnail, dot, keyboard, swipe, variant - goes through one
   go(to, dir). The real gallery is switched INSTANTLY underneath, so its own state
   (counter, thumbnails, dots, aria, zoom/lightbox targets) is right the moment the
   slide starts, and the motion is drawn on a clipped overlay above it: the outgoing
   picture and the incoming one, moved together with transform only (Web Animations
   API). When the push ends the overlay is removed and the animations cancelled, so no
   transform is ever left on the gallery itself.

   forward (+1): the new image enters from the right, the old one leaves to the left.
   back    (-1): the new image enters from the left,  the old one leaves to the right.
   Arrow wrap last->first counts as forward, first->last as back.

   Quick view (product.modal, inside .modal), collection cards, cart images, zoom and
   lightbox, and every other slider are NOT touched. */
(function () {
  'use strict';
  if (window.OBGallerySlide) { return; }

  var DUR = 380;          /* taps, arrows, thumbnails, dots, keyboard, variants */
  var SWIPE_DUR = 285;    /* finish or spring back after the finger lets go      */
  var FADE_DUR = 200;     /* prefers-reduced-motion: cross-fade instead           */
  var EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
  var LOCK = 6;           /* px before a drag counts, and only if |dx| > |dy|     */
  var COMMIT = 0.2;       /* of the width                                        */
  var FLICK_V = 0.45;     /* px/ms                                               */
  var FLICK_PX = 20;
  var EDGE = 0.3;         /* resistance where a gallery has no neighbour to show */

  var canAnimate = typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';
  var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reduced() { return !!(mq && mq.matches); }
  function noop() {}
  function tx(px) { return 'translateX(' + px + 'px)'; }

  /* ---- pictures ---------------------------------------------------------- */

  /* Resolves once every <img> in el has its pixels (or has failed), so an incoming
     picture is never blank mid-slide. Lazy images are promoted to eager first. */
  function ready(el) {
    if (!el) { return Promise.resolve(); }
    var imgs = el.tagName === 'IMG' ? [el] : Array.prototype.slice.call(el.querySelectorAll('img'));
    return Promise.all(imgs.map(function (im) {
      if (im.getAttribute('loading') === 'lazy') { im.setAttribute('loading', 'eager'); }
      var done = function () { return im.decode ? im.decode().catch(noop) : null; };
      if (im.complete && im.naturalWidth) { return done(); }
      return new Promise(function (res) {
        im.addEventListener('load', res, { once: true });
        im.addEventListener('error', res, { once: true });
      }).then(done);
    }));
  }

  /* A non-interactive copy of a slide for the overlay. Keeps src/srcset/sizes so it
     uses the same cached file the gallery already chose. */
  function visualOf(el) {
    var c = el.cloneNode(true);
    var all = [c].concat(Array.prototype.slice.call(c.querySelectorAll('*')));
    all.forEach(function (n) {
      if (n.removeAttribute) {
        n.removeAttribute('id');
        n.removeAttribute('fetchpriority');
        if (n.hasAttribute && n.hasAttribute('tabindex')) { n.setAttribute('tabindex', '-1'); }
      }
      if (n.tagName === 'IMG' && n.getAttribute('loading') === 'lazy') { n.setAttribute('loading', 'eager'); }
      if (n.tagName === 'VIDEO' && n.pause) { try { n.pause(); } catch (e) {} n.removeAttribute('autoplay'); }
    });
    c.setAttribute('aria-hidden', 'true');
    c.setAttribute('inert', '');
    return c;
  }

  function place(el, w, h) {
    var s = el.style;
    s.position = 'absolute';
    s.top = '0';
    s.left = '0';
    s.right = 'auto';
    s.bottom = 'auto';
    s.margin = '0';
    s.width = w + 'px';
    s.height = h + 'px';
    s.maxWidth = 'none';
    s.transform = 'none';
    s.transition = 'none';
    s.opacity = '1';
    s.visibility = 'visible';
    s.zIndex = 'auto';
    s.pointerEvents = 'none';
    return el;
  }

  /* ---- one gallery ------------------------------------------------------- */
  /* spec:
       host        the clipped main image box the push is drawn over
       layer       positioned ancestor that receives the overlay (default: host)
       before()    node the overlay is inserted before inside layer (default: end)
       z           overlay z-index (default: auto - DOM order decides)
       bg          overlay backdrop colour (default: host's image background)
       count()     number of slides
       index()     index showing now
       snapshot()  element showing what is on screen now (a copy)
       visual(i)   element showing slide i (a copy)
       apply(i)    switch the real gallery to i instantly, update counter/thumbs/dots
       settled(i)  promise for the real gallery's pixels at i (optional)
       preload(i)  warm slide i (optional)
       wrapSwipe   whether a swipe past the last image continues to the first
       swipeEl     element that receives touch drags (optional - enables the swipe)
       swipeStop   stop touch pointer events reaching the gallery's own drag code
       height(i)   rendered height of slide i inside host (optional, default host) */
  function Gallery(spec) {
    this.s = spec;
    this.busy = null;
    this.drag = null;
    if (spec.swipeEl) { this.bindSwipe(); }
    /* warm the neighbours (+-1) once the page has loaded, so the first tap or swipe
       never waits and the warming never competes with the page's main image */
    var self = this;
    afterLoad(function () { self.warm(spec.index()); });
  }

  function afterLoad(fn) {
    var go = function () { setTimeout(fn, 300); };
    if (document.readyState === 'complete') { go(); } else { window.addEventListener('load', go, { once: true }); }
  }

  Gallery.prototype.finish = function () {
    var b = this.busy;
    if (b) { b.end(); }
  };

  Gallery.prototype.box = function () {
    var s = this.s, host = s.host, layer = s.layer || host;
    var box = document.createElement('div');
    box.className = 'ob-gs';
    box.setAttribute('aria-hidden', 'true');
    var bs = box.style;
    bs.position = 'absolute';
    bs.overflow = 'hidden';
    bs.pointerEvents = 'none';
    bs.contain = 'paint';
    if (s.z) { bs.zIndex = s.z; }
    var restore = null;
    if (layer !== host) {
      if (getComputedStyle(layer).position === 'static') {
        var prev = layer.style.position;
        layer.style.position = 'relative';
        restore = function () { layer.style.position = prev; };
      }
      var lr = layer.getBoundingClientRect(), hr = host.getBoundingClientRect();
      bs.left = (hr.left - lr.left - layer.clientLeft) + 'px';
      bs.top = (hr.top - lr.top - layer.clientTop) + 'px';
      bs.width = hr.width + 'px';
      bs.height = hr.height + 'px';
    } else {
      bs.left = '0';
      bs.top = '0';
      bs.width = '100%';
      bs.height = '100%';
    }
    var bg = s.bg || '';
    if (!bg) {
      var im = host.querySelector('img');
      var c = im ? getComputedStyle(im).backgroundColor : '';
      bg = c && c !== 'rgba(0, 0, 0, 0)' && c !== 'transparent' ? c : getComputedStyle(host).backgroundColor;
      if (!bg || bg === 'rgba(0, 0, 0, 0)' || bg === 'transparent') { bg = '#fff'; }
    }
    bs.background = bg;
    var ref = s.before ? s.before() : null;
    layer.insertBefore(box, ref && ref.parentNode === layer ? ref : null);
    box._restore = restore;
    return box;
  };

  function dropBox(box) {
    if (!box) { return; }
    if (box.parentNode) { box.parentNode.removeChild(box); }
    if (box._restore) { box._restore(); }
  }

  Gallery.prototype.width = function () { return this.s.host.getBoundingClientRect().width || this.s.host.offsetWidth; };
  Gallery.prototype.hOf = function (i) {
    var h = this.s.height ? this.s.height(i) : 0;
    return h || this.s.host.getBoundingClientRect().height || this.s.host.offsetHeight;
  };

  Gallery.prototype.norm = function (i) {
    var n = this.s.count();
    return ((i % n) + n) % n;
  };

  /* The single path every control goes through. dir: +1 forward, -1 back; when it
     is not given, the index rule decides (thumbnails, dots, variants). */
  Gallery.prototype.go = function (to, dir, opt) {
    opt = opt || {};
    var s = this.s;
    if (this.drag) { this.cancelDrag(); }
    this.finish();
    var from = s.index();
    if (to === from && !opt.force) { return; }
    if (dir === undefined || dir === null) { dir = to > from ? 1 : -1; }
    if (!canAnimate) { s.apply(to); return; }
    var w = this.width();
    var box = this.box();
    var out = place(s.snapshot(), w, this.hOf(from));
    box.appendChild(out);
    s.apply(to);                                   /* counter / thumbs / dots now */
    var inn = place(opt.visual || s.visual(to), w, this.hOf(to));
    box.appendChild(inn);
    this.push(box, out, inn, dir, 0, false, to);
    this.warm(to);
  };

  Gallery.prototype.warm = function (i) {
    var s = this.s, n = s.count();
    if (!s.preload || n < 2) { return; }
    s.preload(this.norm(i + 1));
    s.preload(this.norm(i - 1));
  };

  /* Animate out: from -> -dir*w and in: dir*w+from -> 0, together. */
  Gallery.prototype.push = function (box, out, inn, dir, from, swipe, to, gate) {
    var self = this, s = this.s, w = this.width(), rm = reduced();
    var opt = { duration: rm ? FADE_DUR : (swipe ? SWIPE_DUR : DUR), easing: EASE, fill: 'both' };
    var kIn = rm ? [{ opacity: 0 }, { opacity: 1 }] : [{ transform: tx(dir * w + from) }, { transform: tx(0) }];
    var kOut = rm ? [{ opacity: 1, transform: tx(0) }, { opacity: 0, transform: tx(0) }]
                  : [{ transform: tx(from) }, { transform: tx(-dir * w) }];
    var aIn = inn.animate(kIn, opt), aOut = out.animate(kOut, opt);
    var b = { over: false };
    b.end = function () {
      if (b.over) { return; }
      b.over = true;
      if (self.busy === b) { self.busy = null; }
      try { aIn.finish(); aOut.finish(); } catch (e) {}
      dropBox(box);
      aIn.cancel();
      aOut.cancel();
    };
    this.busy = b;
    /* hold on the first frame until both the incoming copy and the real gallery
       underneath have their pixels - the overlay hides the swap meanwhile */
    aIn.pause();
    aOut.pause();
    Promise.all([ready(inn), s.settled ? s.settled(to) : null, gate || null]).then(function () {
      if (b.over) { return; }
      aIn.play();
      aOut.play();
      aIn.finished.then(function () { b.end(); }, noop);
    });
  };

  /* ---- swipe -------------------------------------------------------------- */
  Gallery.prototype.bindSwipe = function () {
    var self = this, s = this.s, el = s.swipeEl, cap = !!s.swipeStop;
    var t = null, suppress = false;

    function down(e) {
      if (e.pointerType !== 'touch' || (e.button && e.button !== 0)) { return; }
      if (e.target.closest && e.target.closest('button, a, input, select, textarea, [data-no-swipe]')) { return; }
      if (cap) { e.stopPropagation(); }
      self.finish();
      t = { id: e.pointerId, x0: e.clientX, y0: e.clientY, t0: e.timeStamp, dx: 0, locked: null };
    }
    function move(e) {
      if (!t || e.pointerId !== t.id) { return; }
      if (cap) { e.stopPropagation(); }
      var dx = e.clientX - t.x0, dy = e.clientY - t.y0;
      if (t.locked === null) {
        if (Math.abs(dx) <= LOCK && Math.abs(dy) <= LOCK) { return; }
        t.locked = Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > LOCK ? 'x' : 'y';
        if (t.locked === 'y') { t = null; return; }        /* the page scrolls */
        try { el.setPointerCapture(e.pointerId); } catch (err) {}
        self.startDrag();
      }
      t.dx = dx;
      self.moveDrag(dx);
    }
    function up(e) {
      if (!t || e.pointerId !== t.id) { return; }
      if (cap) { e.stopPropagation(); }
      var tt = t; t = null;
      if (tt.locked !== 'x') { return; }
      suppress = true;
      setTimeout(function () { suppress = false; }, 350);
      var adx = Math.abs(tt.dx), w = self.width();
      var dt = Math.max(1, e.timeStamp - tt.t0);
      var dir = 0;
      if (e.type === 'pointerup' && (adx > w * COMMIT || (adx / dt > FLICK_V && adx > FLICK_PX))) {
        dir = tt.dx < 0 ? 1 : -1;
      }
      self.endDrag(dir);
    }
    el.addEventListener('pointerdown', down, cap);
    el.addEventListener('pointermove', move, cap);
    el.addEventListener('pointerup', up, cap);
    el.addEventListener('pointercancel', up, cap);
    /* a swipe is not a tap */
    el.addEventListener('click', function (e) {
      if (suppress) { e.preventDefault(); e.stopPropagation(); }
    }, true);
    if (cap) {
      /* the gallery's own touch handlers never see a touch that started here */
      ['touchstart', 'touchmove', 'touchend'].forEach(function (type) {
        el.addEventListener(type, function (e) { e.stopPropagation(); }, { passive: true });
      });
    }
  };

  Gallery.prototype.has = function (dir) {
    var s = this.s, i = s.index(), n = s.count();
    if (n < 2) { return false; }
    if (s.wrapSwipe) { return true; }
    return dir > 0 ? i < n - 1 : i > 0;
  };

  Gallery.prototype.startDrag = function () {
    var s = this.s, i = s.index(), w = this.width();
    this.finish();
    if (!canAnimate) { this.drag = { box: null }; return; }
    var box = this.box();
    var cur = place(s.snapshot(), w, this.hOf(i));
    box.appendChild(cur);
    var d = { box: box, cur: cur, w: w, i: i, prev: null, next: null, dx: 0 };
    if (!reduced()) {
      if (this.has(-1)) { var p = this.norm(i - 1); d.prev = place(s.visual(p), w, this.hOf(p)); box.appendChild(d.prev); }
      if (this.has(1)) { var q = this.norm(i + 1); d.next = place(s.visual(q), w, this.hOf(q)); box.appendChild(d.next); }
    }
    this.drag = d;
    this.moveDrag(0);
  };

  Gallery.prototype.moveDrag = function (dx) {
    var d = this.drag;
    if (!d || !d.box) { return; }
    if (reduced()) { return; }                       /* no movement under reduced motion */
    if ((dx > 0 && !d.prev) || (dx < 0 && !d.next)) { dx = dx * EDGE; }
    d.dx = dx;
    d.cur.style.transform = tx(dx);                  /* 1:1 with the finger */
    if (d.prev) { d.prev.style.transform = tx(dx - d.w); }
    if (d.next) { d.next.style.transform = tx(dx + d.w); }
  };

  Gallery.prototype.cancelDrag = function () {
    var d = this.drag;
    this.drag = null;
    if (d) { dropBox(d.box); }
  };

  Gallery.prototype.endDrag = function (dir) {
    var d = this.drag, s = this.s;
    this.drag = null;
    if (!d) { return; }
    if (!d.box) {                                     /* no WAAPI: plain switch */
      if (dir) { s.apply(this.norm(s.index() + dir)); }
      return;
    }
    if (dir && !this.has(dir)) { dir = 0; }
    var peek = dir > 0 ? d.next : d.prev;
    if (dir && reduced()) {                           /* commit as a cross-fade */
      var to0 = this.norm(d.i + dir);
      peek = place(s.visual(to0), d.w, this.hOf(to0));
      d.box.appendChild(peek);
    }
    if (dir && peek) {
      var to = this.norm(d.i + dir);
      [d.prev, d.next].forEach(function (g) { if (g && g !== peek && g.parentNode) { g.parentNode.removeChild(g); } });
      d.cur.style.transform = '';
      peek.style.transform = '';
      s.apply(to);                                    /* counter / thumbs / dots now */
      this.push(d.box, d.cur, peek, dir, reduced() ? 0 : d.dx, true, to);
      this.warm(to);
      return;
    }
    /* spring back */
    var self = this, opt = { duration: SWIPE_DUR, easing: EASE, fill: 'both' };
    var anims = [d.cur.animate([{ transform: tx(d.dx) }, { transform: tx(0) }], opt)];
    if (d.prev) { anims.push(d.prev.animate([{ transform: tx(d.dx - d.w) }, { transform: tx(-d.w) }], opt)); }
    if (d.next) { anims.push(d.next.animate([{ transform: tx(d.dx + d.w) }, { transform: tx(d.w) }], opt)); }
    var b = { over: false };
    b.end = function () {
      if (b.over) { return; }
      b.over = true;
      if (self.busy === b) { self.busy = null; }
      dropBox(d.box);
      anims.forEach(function (a) { a.cancel(); });
    };
    this.busy = b;
    anims[0].finished.then(function () { b.end(); }, noop);
  };

  function create(spec) { return new Gallery(spec); }

  /* ---- Impulse / Flickity (main-product) ---------------------------------- */
  function flickityHost(f) {
    return f && f.element && f.element.matches && f.element.matches('[data-product-photos]') &&
           !f.element.closest('.modal') && f.viewport;
  }
  /* Keyed on the element, not the instance: Impulse destroys and rebuilds Flickity on
     the same element (image sets), and the swipe must follow the live instance. */
  function flickityGallery(f, select) {
    var el = f.element;
    el.__obgsF = f;
    if (el.__obgs) { el.__obgs.s.host = f.viewport; return el.__obgs; }   /* rebuilt viewport */
    function live() { return el.__obgsF; }
    function cell(i) { var sl = live().slides[i]; return sl && sl.cells[0] && sl.cells[0].element; }
    el.__obgs = create({
      host: f.viewport,
      count: function () { return live().slides.length; },
      index: function () { return live().selectedIndex; },
      snapshot: function () { return visualOf(cell(live().selectedIndex)); },
      visual: function (i) { return visualOf(cell(i)); },
      apply: function (i) { select.call(live(), i, false, true); },
      settled: function (i) { return ready(cell(i)); },
      preload: function (i) { var c = cell(i); if (c) { ready(c); } },
      height: function (i) { var c = cell(i); return c ? c.offsetHeight : 0; },
      wrapSwipe: !!f.options.wrapAround,
      swipeEl: el,
      swipeStop: true                               /* Flickity's own drag keeps the mouse only */
    });
    return el.__obgs;
  }
  function hookFlickity() {
    var F = window.Flickity;
    if (!F || !F.prototype || F.prototype.__obgsHooked) { return !!F; }
    F.prototype.__obgsHooked = true;
    var select = F.prototype.select;
    F.prototype.select = function (index, isWrap, isInstant) {
      if (isInstant || this.isDragSelect || !canAnimate || !this.isActive || !flickityHost(this)) {
        return select.apply(this, arguments);
      }
      var n = this.slides.length, raw = parseInt(index, 10);
      if (!n || isNaN(raw)) { return select.apply(this, arguments); }
      var to = (this.options.wrapAround || isWrap) ? ((raw % n) + n) % n : Math.max(0, Math.min(n - 1, raw));
      if (to === this.selectedIndex || !this.slides[to]) { return select.apply(this, arguments); }
      /* raw beyond the ends means next() or previous() wrapped: direction is the
         button's, not the index's */
      var dir = raw > this.selectedIndex ? 1 : -1;
      flickityGallery(this, select).go(to, dir);
    };
    /* Galleries already built before this hook get their swipe now. */
    document.querySelectorAll('[data-product-photos].flickity-enabled').forEach(function (el) {
      var f = F.data && F.data(el);
      if (f && flickityHost(f)) { flickityGallery(f, select); }
    });
    /* ...and ones built later (image-set rebuilds, section reloads). */
    var activate = F.prototype.activate;
    if (typeof activate === 'function') {
      F.prototype.activate = function () {
        var r = activate.apply(this, arguments);
        if (flickityHost(this)) { flickityGallery(this, select); }
        return r;
      };
    }
    return true;
  }

  /* ---- <media-gallery> (main-product-high-variant) ------------------------ */
  function mgGallery(m) {
    if (m.__obgs) { return m.__obgs; }
    var main = m.mainContainer;
    m.__obgs = create({
      host: main,
      z: '3',                                          /* above the slides (2), under the arrows (10) */
      count: function () { return m.slides.length; },
      index: function () { return m.currentIndex; },
      snapshot: function () { return visualOf(m.slides[m.currentIndex]); },
      visual: function (i) { return visualOf(m.slides[i]); },
      apply: function (i) { mgApply(m, i); },
      settled: function (i) { return ready(m.slides[i]); },
      preload: function (i) { ready(m.slides[i]); },
      wrapSwipe: true,                                 /* its own swipe wrapped */
      swipeEl: main,
      swipeStop: true
    });
    return m.__obgs;
  }
  /* Its own goToSlide, without the transition: the same classes and state, instantly. */
  function mgApply(m, i) {
    var cur = m.slides[m.currentIndex], target = m.slides[i];
    if (!target || cur === target) { return; }
    if (m.pauseAllMediaExceptInSlide) { m.pauseAllMediaExceptInSlide(target); }
    if (m.resetSlides) { m.resetSlides(); }
    m.slides.forEach(function (sl) { sl.classList.remove('media-gallery__slide--is-animating'); });
    cur.classList.remove('media-gallery__slide--active');
    cur.style.transform = 'translateX(100%)';
    target.style.transform = 'translateX(0)';
    target.classList.add('media-gallery__slide--active');
    if (m.updateThumbnails) { m.updateThumbnails(i); }
    m.currentIndex = i;
    m.isAnimating = false;
    if (m.isNaturalAspectRatio && m.adjustContainerHeight) { m.adjustContainerHeight(); }
    if (m.scrollActiveThumbnailIntoView) { try { m.scrollActiveThumbnailIntoView(); } catch (e) {} }
    if (m.pauseNonActiveSlides) { m.pauseNonActiveSlides(); }
    if (m.autoplayActiveSlideIfEligible) { m.autoplayActiveSlideIfEligible(); }
  }
  function hookMediaGallery() {
    if (!window.customElements) { return; }
    customElements.whenDefined('media-gallery').then(function () {
      var C = customElements.get('media-gallery');
      if (!C || C.prototype.__obgsHooked) { return; }
      C.prototype.__obgsHooked = true;
      var orig = C.prototype.goToSlide;
      C.prototype.goToSlide = function (index, direction) {
        if (!canAnimate || !this.mainContainer || this.closest('.modal')) { return orig.apply(this, arguments); }
        if (index < 0 || index >= this.slides.length || index === this.currentIndex) { return; }
        mgGallery(this).go(index, direction === 'prev' ? -1 : 1);
      };
      document.querySelectorAll('media-gallery').forEach(function (m) { if (m.mainContainer) { mgGallery(m); } });
      watchHotReload();
    });
  }
  /* A variant change on this template re-renders the whole section (product-hot-reload,
     inside a View Transition). The old gallery's picture slides out and the new one's
     slides in, the direction taken from the two images' positions in product.media. */
  function watchHotReload() {
    if (!window.MutationObserver) { return; }
    document.querySelectorAll('product-hot-reload').forEach(function (hr) {
      if (hr.__obgs) { return; }
      hr.__obgs = true;
      new MutationObserver(function (list) {
        var oldG = null, newG = null;
        list.forEach(function (r) {
          Array.prototype.forEach.call(r.removedNodes, function (n) {
            if (n.nodeType === 1) { oldG = oldG || (n.matches('media-gallery') ? n : n.querySelector('media-gallery')); }
          });
          Array.prototype.forEach.call(r.addedNodes, function (n) {
            if (n.nodeType === 1) { newG = newG || (n.matches('media-gallery') ? n : n.querySelector('media-gallery')); }
          });
        });
        if (!oldG || !newG || !canAnimate || !oldG.slides || !newG.slides) { return; }
        var oldSlide = oldG.slides[oldG.currentIndex || 0], newSlide = newG.slides[newG.currentIndex || 0];
        if (!oldSlide || !newSlide) { return; }
        var po = parseInt(oldSlide.getAttribute('data-media-position'), 10);
        var pn = parseInt(newSlide.getAttribute('data-media-position'), 10);
        if (isNaN(po) || isNaN(pn) || po === pn) { return; }
        var g = mgGallery(newG);
        var w = g.width(), box = g.box();
        var out = place(visualOf(oldSlide), w, g.hOf(0));
        box.appendChild(out);
        var inn = place(visualOf(newSlide), w, g.hOf(0));
        box.appendChild(inn);
        /* the re-render lands inside a View Transition, which holds rendering while it
           captures; start the push once the new state is painting, so none of it is spent
           unseen */
        var painted = new Promise(function (res) { requestAnimationFrame(function () { requestAnimationFrame(res); }); });
        g.push(box, out, inn, pn > po ? 1 : -1, 0, false, newG.currentIndex || 0, painted);
      }).observe(hr, { childList: true });
    });
  }

  window.OBGallerySlide = {
    create: create,
    visualOf: visualOf,
    ready: ready,
    reduced: reduced,
    DUR: DUR, SWIPE_DUR: SWIPE_DUR, FADE_DUR: FADE_DUR, EASE: EASE
  };

  if (!hookFlickity()) {
    document.addEventListener('DOMContentLoaded', hookFlickity);
  }
  hookMediaGallery();
})();
